import { FC, useCallback, useEffect, useMemo, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import io from 'socket.io-client';
import { Socket } from 'socket.io-client/build/esm/socket';
import { useSnackbar } from 'notistack';
import { useTranslation } from 'react-i18next';
import { CurrentUserReducer, RootReducer, StreamingReducer } from '../../../../models/Redux';
import { HOST_URL, SOCKETIO_ENDPOINT } from '../../../../environment/host-config/active';
import {
    FILTER_USER_MESSAGES_STREAMING_CHAT_MESSAGE,
    PUSH_STREAMING_CHAT_MESSAGE,
    thunkGetStreamingStateEvent,
} from '../../../../store/actions/Streaming.actions';
import { ChatMessage } from '../../../../models/StreamingState';

const SocketIoManager: FC = () => {
    const dispatch = useDispatch();
    const { t } = useTranslation();
    const { enqueueSnackbar } = useSnackbar();
    const isConnecting = useRef<boolean>(false);

    const {
        user: { user_username: userUsername, user_ide: userIde },
    } = useSelector<RootReducer, CurrentUserReducer>(state => state.currentUser);
    const {
        eventUuid,
        eventData: {
            user: { user_ide: influencerUserIde },
        },
        streamingFlags: { started },
        localStreamingState: { room },
    } = useSelector<RootReducer, StreamingReducer>(state => state.streaming);

    const isEventInfluencer = useMemo(() => influencerUserIde === userIde, [influencerUserIde, userIde]);

    const disconnectEvent = useCallback(() => {
        try {
            room?.disconnect();
        } catch (e) {}
        window.location.href = `${HOST_URL}/event/${eventUuid}`;
    }, [eventUuid, room]);

    const parseUserDataForSocketIOSignal = useCallback(() => {
        let responseData = null;

        if (isEventInfluencer) {
            responseData = {
                influencerData: {
                    username: userUsername,
                    userId: userIde,
                },
                uuid: eventUuid,
            };
        } else {
            responseData = {
                viewerData: {
                    username: userUsername,
                    userId: userIde,
                },
                uuid: eventUuid,
            };
        }

        return responseData;
    }, [eventUuid, isEventInfluencer, userIde, userUsername]);

    const updateStreamingState = useCallback(() => {
        dispatch(thunkGetStreamingStateEvent());
    }, [dispatch]);

    const filterBannedUserMessages = useCallback(
        (username: string) => {
            dispatch({ type: FILTER_USER_MESSAGES_STREAMING_CHAT_MESSAGE, payload: username });
        },
        [dispatch],
    );

    const pushChatMessage = useCallback(
        (message: ChatMessage) => {
            dispatch({ type: PUSH_STREAMING_CHAT_MESSAGE, payload: message });
        },
        [dispatch],
    );

    const addSocketHandlers = useCallback(
        (localSocket: Socket) => {
            try {
                localSocket.on('connect', () => {
                    localSocket.emit('connectOk', parseUserDataForSocketIOSignal());
                    localSocket.emit('event', parseUserDataForSocketIOSignal());
                });

                localSocket.on('eventOk', () => {});

                localSocket.on('updateEvent', () => {
                    updateStreamingState();
                    localSocket.emit('updateEventOk', parseUserDataForSocketIOSignal());
                });

                localSocket.on('newMessage', (data: ChatMessage) => {
                    localSocket.emit('newMessageOk', parseUserDataForSocketIOSignal());
                    pushChatMessage(data);
                });

                localSocket.on('userBanned', (data: any) => {
                    if (userUsername === data.usernameBanned) {
                        if (data.blocked) {
                            enqueueSnackbar(
                                t(
                                    'You have been blocked from this event. You will not be able to send messages using the chat until an admnistrator will unblock you again.',
                                ),
                                { variant: 'warning', autoHideDuration: 10000 },
                            );
                        } else {
                            enqueueSnackbar(
                                t(
                                    'You have been unblocked from this event. You will be able to send messages again.',
                                ),
                                { variant: 'success', autoHideDuration: 10000 },
                            );
                        }
                    }

                    filterBannedUserMessages(data.usernameBanned);
                    localSocket.emit('userBannedOk', parseUserDataForSocketIOSignal());
                });

                localSocket.on('finishEvent', () => {
                    enqueueSnackbar(
                        'The organizer has finished the event. Thank you for your assistance and we hope to see you soon. You will be redirected automatically.',
                        { variant: 'success', autoHideDuration: 10000 },
                    );
                    setTimeout(() => {
                        disconnectEvent();
                    }, 10000);

                    localSocket.emit('finishEventOk', parseUserDataForSocketIOSignal());
                });
            } catch (e) {}
        },
        [
            disconnectEvent,
            enqueueSnackbar,
            filterBannedUserMessages,
            parseUserDataForSocketIOSignal,
            pushChatMessage,
            t,
            updateStreamingState,
            userUsername,
        ],
    );

    const connectSocketIO = useCallback(() => {
        try {
            // @ts-ignore
            const localSocket = io.connect(SOCKETIO_ENDPOINT);

            addSocketHandlers(localSocket);
        } catch (e) {}
    }, [addSocketHandlers]);

    const disconnectSocketIO = () => {};

    useEffect(() => {
        if (started && !isConnecting.current) {
            isConnecting.current = true;
            connectSocketIO();
        } else {
            disconnectSocketIO();
        }
    }, [connectSocketIO, started]);

    return null;
};

export default SocketIoManager;
